import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Switch } from '../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { useToast } from '../hooks/useToast';
import api from '../services/api';

interface SystemConfig {
  key: string;
  value: any;
  description: string;
  updatedAt: string;
}

interface MinioConfig {
  endpoint: string;
  accessKey: string;
  secretKey: string;
}

interface AppSettings {
  maintenanceMode: boolean;
  maxUploadSize: string;
  sessionTimeout: number;
  backupRetentionDays: number;
}

interface NotificationSettings {
  emailEnabled: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  adminEmail: string;
}

interface MainWPConfig {
  mainwpUrl: string;
  apiKey: string;
  enabled: boolean;
  checkInterval: number;
}

export default function SystemConfigPage() {
  const [configs, setConfigs] = useState<SystemConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minioConfig, setMinioConfig] = useState<MinioConfig>({
    endpoint: '',
    accessKey: '',
    secretKey: ''
  });
  const [appSettings, setAppSettings] = useState<AppSettings>({
    maintenanceMode: false,
    maxUploadSize: '100MB',
    sessionTimeout: 3600,
    backupRetentionDays: 30
  });
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailEnabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    adminEmail: 'admin@codecolliders.dev'
  });
  const [mainwpConfig, setMainwpConfig] = useState<MainWPConfig>({
    mainwpUrl: '',
    apiKey: '',
    enabled: false,
    checkInterval: 86400
  });

  const { toast } = useToast();

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/system-config');
      setConfigs(response.data);

      // Extraire les configurations spécifiques
      const minioConfig = response.data.find((c: SystemConfig) => c.key === 'minio');
      if (minioConfig) {
        setMinioConfig(minioConfig.value);
      }

      const appConfig = response.data.find((c: SystemConfig) => c.key === 'app_settings');
      if (appConfig) {
        setAppSettings(appConfig.value);
      }

      const notifConfig = response.data.find((c: SystemConfig) => c.key === 'notifications');
      if (notifConfig) {
        setNotificationSettings(notifConfig.value);
      }

      const mainwpConfigData = response.data.find((c: SystemConfig) => c.key === 'mainwp');
      if (mainwpConfigData) {
        setMainwpConfig(mainwpConfigData.value);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger la configuration système',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveMinioConfig = async () => {
    try {
      setSaving(true);
      await api.put('/system-config/minio', {
        value: minioConfig,
        description: 'Configuration du serveur MinIO pour les sauvegardes'
      });
      
      toast({
        title: 'Succès',
        description: 'Configuration MinIO sauvegardée'
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde MinIO:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la configuration MinIO',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const saveMainWPConfig = async () => {
    try {
      setSaving(true);
      await api.put('/system-config/mainwp', {
        value: mainwpConfig,
        description: 'Configuration pour l\'accès à l\'API REST v2 de MainWP. L\'authentification se fait avec Bearer Token.'
      });
      
      toast({
        title: 'Succès',
        description: 'Configuration MainWP sauvegardée'
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde MainWP:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la configuration MainWP',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const saveAppSettings = async () => {
    try {
      setSaving(true);
      await api.put('/system-config/app_settings', {
        value: appSettings,
        description: 'Paramètres généraux de l\'application'
      });
      
      toast({
        title: 'Succès',
        description: 'Paramètres de l\'application sauvegardés'
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder les paramètres',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    try {
      setSaving(true);
      await api.put('/system-config/notifications', {
        value: notificationSettings,
        description: 'Configuration des notifications par email'
      });
      
      toast({
        title: 'Succès',
        description: 'Configuration des notifications sauvegardée'
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des notifications:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la configuration des notifications',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const testMinioConnection = async () => {
    try {
      const response = await api.post('/system-config/minio/test');
      if (response.data.valid) {
        toast({
          title: 'Connexion réussie',
          description: 'La configuration MinIO est valide'
        });
      } else {
        toast({
          title: 'Connexion échouée',
          description: 'Vérifiez votre configuration MinIO',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de tester la connexion MinIO',
        variant: 'destructive'
      });
    }
  };

  const testMainWPConnection = async () => {
    try {
      const response = await api.post('/system-config/mainwp/test');
      if (response.data.valid) {
        toast({
          title: 'Connexion réussie',
          description: `Configuration MainWP valide (${response.data.sitesCount || 0} sites trouvés)`
        });
      } else {
        toast({
          title: 'Connexion échouée',
          description: response.data.message || 'Vérifiez votre configuration MainWP',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de tester la connexion MainWP',
        variant: 'destructive'
      });
    }
  };

  const initializeDefaultConfig = async () => {
    try {
      setLoading(true);
      await api.post('/system-config/initialize');
      await loadConfigs();
      toast({
        title: 'Succès',
        description: 'Configuration par défaut initialisée'
      });
    } catch (error) {
      console.error('Erreur lors de l\'initialisation:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'initialiser la configuration par défaut',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Chargement de la configuration...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Configuration Système</h1>
        <Button onClick={initializeDefaultConfig} variant="outline">
          Initialiser la config par défaut
        </Button>
      </div>

      <Tabs defaultValue="minio" className="space-y-4">
        <TabsList>
          <TabsTrigger value="minio">MinIO</TabsTrigger>
          <TabsTrigger value="mainwp">MainWP</TabsTrigger>
          <TabsTrigger value="app">Application</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="minio" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration MinIO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="endpoint">Endpoint</Label>
                <Input
                  id="endpoint"
                  value={minioConfig.endpoint}
                  onChange={(e) => setMinioConfig(prev => ({ ...prev, endpoint: e.target.value }))}
                  placeholder="minio.collide.rs"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="accessKey">Clé d'accès</Label>
                  <Input
                    id="accessKey"
                    type="password"
                    value={minioConfig.accessKey}
                    onChange={(e) => setMinioConfig(prev => ({ ...prev, accessKey: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="secretKey">Clé secrète</Label>
                  <Input
                    id="secretKey"
                    type="password"
                    value={minioConfig.secretKey}
                    onChange={(e) => setMinioConfig(prev => ({ ...prev, secretKey: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex space-x-2">
                <Button onClick={saveMinioConfig} disabled={saving}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
                <Button onClick={testMinioConnection} variant="outline">
                  Tester la connexion
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mainwp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration MainWP</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="mainwpEnabled"
                  checked={mainwpConfig.enabled}
                  onCheckedChange={(checked) => setMainwpConfig(prev => ({ ...prev, enabled: checked }))}
                />
                <Label htmlFor="mainwpEnabled">Activer MainWP</Label>
              </div>

              <div>
                <Label htmlFor="mainwpUrl">URL MainWP</Label>
                <Input
                  id="mainwpUrl"
                  value={mainwpConfig.mainwpUrl}
                  onChange={(e) => setMainwpConfig(prev => ({ ...prev, mainwpUrl: e.target.value }))}
                  placeholder="https://main.example.com"
                />
              </div>

              <div>
                <Label htmlFor="apiKey">Clé API</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={mainwpConfig.apiKey}
                  onChange={(e) => setMainwpConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Clé API MainWP"
                />
              </div>

              <div>
                <Label htmlFor="checkInterval">Intervalle de vérification (secondes)</Label>
                <Input
                  id="checkInterval"
                  type="number"
                  value={mainwpConfig.checkInterval}
                  onChange={(e) => setMainwpConfig(prev => ({ ...prev, checkInterval: parseInt(e.target.value) }))}
                />
              </div>

              <div className="flex space-x-2">
                <Button onClick={saveMainWPConfig} disabled={saving}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
                <Button onClick={testMainWPConnection} variant="outline">
                  Tester la connexion
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="app" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Paramètres de l'Application</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="maintenanceMode"
                  checked={appSettings.maintenanceMode}
                  onCheckedChange={(checked) => setAppSettings(prev => ({ ...prev, maintenanceMode: checked }))}
                />
                <Label htmlFor="maintenanceMode">Mode maintenance</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maxUploadSize">Taille max upload</Label>
                  <Input
                    id="maxUploadSize"
                    value={appSettings.maxUploadSize}
                    onChange={(e) => setAppSettings(prev => ({ ...prev, maxUploadSize: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="sessionTimeout">Timeout session (secondes)</Label>
                  <Input
                    id="sessionTimeout"
                    type="number"
                    value={appSettings.sessionTimeout}
                    onChange={(e) => setAppSettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="backupRetentionDays">Rétention sauvegardes (jours)</Label>
                <Input
                  id="backupRetentionDays"
                  type="number"
                  value={appSettings.backupRetentionDays}
                  onChange={(e) => setAppSettings(prev => ({ ...prev, backupRetentionDays: parseInt(e.target.value) }))}
                />
              </div>

              <Button onClick={saveAppSettings} disabled={saving}>
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration des Notifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="emailEnabled"
                  checked={notificationSettings.emailEnabled}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, emailEnabled: checked }))}
                />
                <Label htmlFor="emailEnabled">Activer les notifications par email</Label>
              </div>

              {notificationSettings.emailEnabled && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtpHost">Serveur SMTP</Label>
                      <Input
                        id="smtpHost"
                        value={notificationSettings.smtpHost}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, smtpHost: e.target.value }))}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="smtpPort">Port SMTP</Label>
                      <Input
                        id="smtpPort"
                        type="number"
                        value={notificationSettings.smtpPort}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, smtpPort: parseInt(e.target.value) }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtpUser">Utilisateur SMTP</Label>
                      <Input
                        id="smtpUser"
                        value={notificationSettings.smtpUser}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, smtpUser: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="smtpPassword">Mot de passe SMTP</Label>
                      <Input
                        id="smtpPassword"
                        type="password"
                        value={notificationSettings.smtpPassword}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, smtpPassword: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="adminEmail">Email administrateur</Label>
                    <Input
                      id="adminEmail"
                      type="email"
                      value={notificationSettings.adminEmail}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, adminEmail: e.target.value }))}
                    />
                  </div>
                </>
              )}

              <Button onClick={saveNotificationSettings} disabled={saving}>
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
